import type { SubletListPriceMarkupInfo } from '../types/invoice';

function formatMarkupPercent(markupPercentage: number): string {
    const whole = Math.round(markupPercentage * 1000) / 10;
    return Number.isInteger(whole) ? `${whole}%` : `${whole}%`;
}

function formatResolvedKeys(markup: SubletListPriceMarkupInfo): string | null {
    const parts: string[] = [];
    if (markup.insuranceCompanyName) {
        parts.push(`insurance: ${markup.insuranceCompanyName}`);
    } else if (markup.insuranceCompanyId) {
        parts.push('insurance: (unresolved name)');
    }
    if (markup.supplierName) {
        parts.push(`vendor: ${markup.supplierName}`);
    } else if (markup.supplierId) {
        parts.push('vendor: (unresolved name)');
    }
    if (markup.state) {
        parts.push(`state: ${markup.state} (shop location)`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
}

function formatSettingsRowMissReason(markup: SubletListPriceMarkupInfo): string {
    switch (markup.settingsRowMissReason) {
        case 'missing_insurance':
            return 'Insurance company could not be resolved from the NEX repair order, so no insurance + vendor + state row could be matched.';
        case 'missing_supplier':
            return 'Vendor could not be matched to a Yaxle supplier, so no insurance + vendor + state row could be matched.';
        case 'missing_state':
            return 'Shop location state is not set, so no insurance + vendor + state row could be matched.';
        case 'no_matching_row':
            return 'No Parts-specific and sublet-specific markups row matched this invoice\'s insurance company, vendor, and shop state.';
        default:
            return 'No Parts-specific and sublet-specific markups row matched this invoice.';
    }
}

/** Multi-line tooltip explaining sublet list price markup for the invoice detail table. */
export function formatSubletListPriceTooltip(markup: SubletListPriceMarkupInfo | null | undefined): string {
    const percentLabel = formatMarkupPercent(markup?.markupPercentage ?? 0);
    const resolvedKeys = markup ? formatResolvedKeys(markup) : null;
    const lines = [
        markup?.preferInvoiceListPrice
            ? 'List price = invoice list price when extracted; otherwise Buy price × (1 + markup)'
            : 'List price = Buy price × (1 + markup)',
        '',
        `Markup applied: ${percentLabel}`,
    ];

    if (!markup) {
        lines.push('', 'Markup details are not available for this invoice yet.');
        return lines.join('\n');
    }

    if (markup.preferInvoiceListPrice) {
        lines.push(
            'Prefer invoice list price: on — lines with an extracted list price keep that value instead of the calculated markup.',
        );
    }

    if (markup.source === 'settings_row') {
        lines.push('Source: Parts-specific and sublet-specific markups (insurance + vendor + state).');
        if (resolvedKeys) {
            lines.push(`Matched: ${resolvedKeys}`);
        }
        return lines.join('\n');
    }

    if (markup.source === 'default') {
        lines.push('Source: Default sublet markup in organization settings.');
        lines.push(`Why: ${formatSettingsRowMissReason(markup)}`);
        if (resolvedKeys) {
            lines.push(`Resolved for: ${resolvedKeys}`);
        }
        return lines.join('\n');
    }

    lines.push('Source: No markup configured — invoice list and buy prices are left as extracted.');
    lines.push(`Why: ${formatSettingsRowMissReason(markup)}`);
    if (resolvedKeys) {
        lines.push(`Resolved for: ${resolvedKeys}`);
    }
    return lines.join('\n');
}
